import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { contactsQueryKeys, getContact, type ApiContact } from '@api/contacts';

// Contacts list rows omit billing_addresses (ContactSerializer#to_list_h) —
// only to_detail_h carries the saved rows — so selecting a contact needs an
// extra detail fetch before a real billing address can be prefilled. 30s
// keeps re-selecting the same contact within a session from re-fetching.
export const CONTACT_DETAIL_STALE_TIME_MS = 30_000;

interface UseContactBillingDetailOptions<TExtra> {
  onResolved: (contact: ApiContact, extra: TExtra) => void;
  onError?: (extra: TExtra) => void;
}

// Fetches full contact detail (for billing_addresses) behind a request-id
// guard: a resolve/reject that arrives after the contact was switched again,
// or after unmount, is silently discarded instead of clobbering newer state.
// Shared by CreateOrderContext and EditOrderDetailsForm, which both prefill a
// billing-address combobox from a selected contact's saved addresses.
export function useContactBillingDetail<TExtra = void>({
  onResolved,
  onError,
}: UseContactBillingDetailOptions<TExtra>) {
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const detailRequestRef = useRef(0);
  const mountedRef = useRef(true);

  // Read via refs (not deps) so fetchDetail/cancel stay referentially stable
  // across renders even though callers usually pass inline callbacks —
  // otherwise every render would hand consumers a new fetchDetail, cascading
  // into new selectContact/actions/context-value identities on every render.
  const onResolvedRef = useRef(onResolved);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onResolvedRef.current = onResolved;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      detailRequestRef.current += 1;
    };
  }, []);

  const fetchDetail = useCallback((contactId: number, extra: TExtra) => {
    const requestId = ++detailRequestRef.current;
    setIsLoading(true);

    void (async () => {
      try {
        const response = await queryClient.fetchQuery({
          queryKey: contactsQueryKeys.detail(contactId),
          queryFn: () => getContact(contactId),
          staleTime: CONTACT_DETAIL_STALE_TIME_MS,
        });

        if (!mountedRef.current || detailRequestRef.current !== requestId) return;
        onResolvedRef.current(response.contact, extra);
      } catch {
        if (mountedRef.current && detailRequestRef.current === requestId) onErrorRef.current?.(extra);
      } finally {
        if (mountedRef.current && detailRequestRef.current === requestId) setIsLoading(false);
      }
    })();
  }, [queryClient]);

  // Discards any in-flight fetch (e.g. contact cleared, dialog reset) without
  // starting a new one.
  const cancel = useCallback(() => {
    detailRequestRef.current += 1;
    setIsLoading(false);
  }, []);

  return { isLoading, fetchDetail, cancel };
}
